import uniqBy from 'lodash/uniqBy';
import ObjectUtils from '@spothero/utils/object';
import SentryUtils from '@spothero/utils/sentry';
import {initialState as userInitialState} from 'user/user-reducer';
import FormatUtils from 'utils/format';
import GTMUtils from 'utils/gtm';
import SegmentUtils from 'utils/segment';

const UserUtils = {
    AUTH_STATE: {
        USER: 'user',
        GUEST: 'guest',
        LOGGED_OUT: 'logged-out',
    },

    INCLUDES:
        'phone_number,admin,is_seller,credit_cards,license_plates,vehicles,favorites,date_joined,has_usable_password',

    CURRENCY_TYPES: {
        USD: 'USD',
        CAD: 'CAD',
    },

    identify(user = null) {
        SegmentUtils.identify(user);
        GTMUtils.identify(user);
        SentryUtils.setUser(
            user
                ? {
                      email: user.email,
                      id: user.id,
                  }
                : {}
        );
    },

    /**
     * @param {Object} user - User data object as received from the API
     * @returns {Object} - Converted fields to camelCase and app field names
     */
    formatPayload: user => {
        if (!user) {
            throw new Error('No user from payload');
        }

        const {
            spotheroCredit, // eslint-disable-line no-unused-vars
            admin: isAdmin,
            creditCards = [],
            creditWallets = [],
            licensePlates = [],
            vehicles = [],
            favorites = [],
            ...formattedPayload
        } = ObjectUtils.camelizeKeys(user);

        return UserUtils.normalize(
            {
                ...formattedPayload,
                isAdmin,
                creditCards: creditCards
                    .filter(card => !card.deleted)
                    .map(card => ({
                        id: card.cardId,
                        type: card.cardType,
                        last4: card.cardLast4,
                        isDefault: card.isDefault,
                    })),
                creditWallets: Object.keys(UserUtils.CURRENCY_TYPES).reduce(
                    (wallets, currencyType) => {
                        const wallet =
                            creditWallets.find(
                                w =>
                                    w.currencyType.toUpperCase() ===
                                    currencyType
                            ) || {};

                        return {
                            ...wallets,
                            [currencyType]: {
                                credit: wallet.amount || 0,
                                currencySymbol: FormatUtils.currencySymbol(
                                    currencyType
                                ),
                                currencyType:
                                    UserUtils.CURRENCY_TYPES[currencyType],
                            },
                        };
                    },
                    {}
                ),
                licensePlates: licensePlates.map(
                    ({licensePlateId: id, ...plateData}) => ({
                        ...plateData,
                        id,
                    })
                ),
                favorites: favorites.map(({id}) => ({
                    id,
                })),
                vehicles: uniqBy(vehicles, vehicle => {
                    return vehicle.id;
                }).map(vehicle => {
                    const vehicleInfo = vehicle.info;
                    const vehiclePlate = vehicle.licensePlate;
                    const vehicleData = {
                        vehicleProfileId: vehicle.id,
                        isDefault: vehicle.isDefault,
                    };

                    if (vehiclePlate) {
                        vehicleData.licensePlateValue = vehiclePlate.value;
                        vehicleData.licensePlateState = vehiclePlate.state;
                    }

                    if (vehicleInfo) {
                        vehicleData.id = vehicleInfo.id;
                        vehicleData.vehicleInfoId = vehicleInfo.id;
                        vehicleData.make = vehicleInfo.make;
                        vehicleData.makeLabel = vehicleInfo.displayMake;
                        vehicleData.model = vehicleInfo.model;
                        vehicleData.modelLabel = vehicleInfo.displayModel;
                        vehicleData.oversize = vehicleInfo.oversize;
                    }

                    return vehicleData;
                }),
            },
            {
                resetToDefaults: true,
            }
        );
    },

    /**
     * Filters data in order to merge into store
     * Option to fill in missing/empty data with defaults to overwrite store
     *
     * @param {Object} data - Formatted user data
     * @param {Boolean} resetToDefaults - When true, overwrites missing values in data with defaults
     * @returns {Object} - Formatted user data to merge into or overwrite store
     */
    normalize(data, {resetToDefaults} = {}) {
        return Object.keys(data).reduce(
            (obj, key) => ({
                ...obj,
                ...(data[key] && {[key]: data[key]}),
            }),
            resetToDefaults ? userInitialState().data : {}
        );
    },
};

export default UserUtils;
